import { Component, ElementRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { SelectComponent } from "@ng-falcon/controls/select/select.component";
import { ConnectedPosition } from "@angular/cdk/overlay";
import { PillListComponent } from "@ng-falcon/pill/pill-list/pill-list.component";

@Component({
    selector: 'ngf-multi-select-pills',
    templateUrl: './multi-select-pills.component.html',
    styleUrls: ['./multi-select-pills.component.scss'],
    standalone: false
})
export class MultiSelectPillsComponent {

  @Input()
  values: any[];

  @Output()
  remove = new EventEmitter<any>();

  @Output()
  clearAll = new EventEmitter<void>();

  static readonly PILLS_CONTAINER_OFFSET_PX = 14;
  readonly collapsedPillTruncateThreshold = 40;
  readonly pillTruncateThreshold = 20;

  positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: MultiSelectPillsComponent.PILLS_CONTAINER_OFFSET_PX,
      panelClass: PillListComponent.POSITION_BOTTOM_PANEL_CLASS
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -MultiSelectPillsComponent.PILLS_CONTAINER_OFFSET_PX,
      panelClass: PillListComponent.POSITION_TOP_PANEL_CLASS
    },
  ];

  // How long after a removal to keep focus on the pills through further re-renders, in animation
  // frames (~2s at 60fps).
  private static readonly KEEP_FOCUS_FRAMES = 120;

  protected readonly select = inject(SelectComponent);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  handleRemove($event: MouseEvent, value: any) {
    $event.stopPropagation();
    this.keepFocusAfterRemove($event.target as HTMLElement | null);
    this.remove.emit(value);
  }

  /**
   * The responsive pill list rebuilds every pill whenever its pills change or its host resizes, so
   * removing a pill destroys the focused remove button and focus drops to <body> — outside the
   * "+X" collapsed pills dialog when the pill was removed from there, which stays open
   * (FLO-50622). Once the removed button is gone, focus the remove button that took its place in
   * the same list (inline or dialog), or the new last one; if none are left, e.g. the last
   * collapsed pill closed the dialog with it, fall back to the combobox.
   *
   * The list can rebuild again shortly after, e.g. when the filtered page reloads and the layout
   * shifts, so for a short while keep putting focus back on that same pill whenever it's lost.
   * Stop as soon as the user moves focus elsewhere or clicks, so focus is never pulled back.
   */
  private keepFocusAfterRemove(target: HTMLElement | null): void {
    const removeBtn = target?.closest<HTMLElement>('.remove-btn');
    const list = removeBtn?.closest<HTMLElement>('.ngf-collapsed-pills-container, ngf-pill-list');
    if (!removeBtn || !list) {
      return;
    }
    const index = this.getRemoveButtons(list).indexOf(removeBtn);
    let focused: HTMLElement | null = null;
    let frames = 0;
    let stopped = false;
    const stop = () => stopped = true;
    document.addEventListener('pointerdown', stop, { capture: true, once: true });

    const restore = () => {
      const active = document.activeElement;
      const focusLost = !active || active === document.body;
      if (!stopped && (focusLost || active === removeBtn || active === focused)) {
        if (focusLost && !removeBtn.isConnected) {
          focused = this.findFocusTarget(list, index, focused?.getAttribute('aria-label'));
          focused?.focus();
        }
        if (++frames < MultiSelectPillsComponent.KEEP_FOCUS_FRAMES) {
          requestAnimationFrame(restore);
          return;
        }
      }
      document.removeEventListener('pointerdown', stop, { capture: true });
    };
    requestAnimationFrame(restore);
  }

  /**
   * The pill focused after the removal (found again by label once the list rebuilds), else the
   * remove button at the removed pill's position, else the combobox.
   */
  private findFocusTarget(list: HTMLElement, index: number, label: string | null | undefined): HTMLElement | null {
    const buttons = list.isConnected ? this.getRemoveButtons(list) : [];
    return buttons.find(button => !!label && button.getAttribute('aria-label') === label)
      ?? buttons[Math.min(index, buttons.length - 1)]
      ?? this.elementRef.nativeElement.closest('ngf-select-input')?.querySelector<HTMLElement>('input')
      ?? null;
  }

  private getRemoveButtons(list: HTMLElement): HTMLElement[] {
    return Array.from(list.querySelectorAll<HTMLElement>('.remove-btn'));
  }
}
